import {partialPickupText} from '../work/partial-pickup-schema'
import type {BoardLane} from './board-lanes'
import type {CommitmentJobDisplay} from './commitment-job-display'
import {DRIVER_SEND_STATUS} from './driver-send-status'
import {resolveJobRequirements, type TimelineJobRow} from './job-rows'
import type {OperationalState} from './operational-state'
import {terminalScheduleLabelFromContext} from './schedule-language'
import type {TerminalRowEntry} from './terminal-jobs'
import type {RequirementKind, ScheduleDate, ViewRange} from './types'

/** Filled vs. required counts for one resource category on a job. */
export type ResourceProgress = {
    filled: number
    required: number
    complete: boolean
}

export type StatusTone = 'critical' | 'attention' | 'calm'

export type JobCardHorizonContext = {
    visibleDays: ScheduleDate[]
    viewRange: ViewRange
    nowHour?: number
}

/**
 * Layout-agnostic view model for a job on the board. Every widescreen layout
 * renders this same summary at a different density, so operational meaning
 * stays identical across layouts.
 */
export type JobCardSummary = {
    title: string
    jobCode: string
    customer: string
    internalContactName: string | null
    scheduleAnchor: string
    scheduleStatus?: 'Overdue'
    scheduleDuration?: string
    scheduleIsTime?: boolean
    spansMultipleDays: boolean
    lane: BoardLane
    locationLine?: string
    crewLine?: string
    drivers: ResourceProgress | null
    equipment: ResourceProgress | null
    assignedNames: string[]
    outstanding: string[]
    unnotifiedDriverCount: number
    statusLine: string
    statusTone: StatusTone
    partialPickupLine?: string
    outOfStateLabel?: string
    noYard?: boolean
    /** Rentals put on rent automatically that dispatch has not confirmed went out. */
    notConfirmedCount?: number
}

function progressForKind(job: TimelineJobRow, kind: RequirementKind): ResourceProgress | null {
    const required = resolveJobRequirements(job)
        .filter((requirement) => requirement.kind === kind)
        .reduce((total, requirement) => total + requirement.quantity, 0)
    const filled = job.allocations.filter((allocation) => allocation.kind === kind).length

    if (required === 0 && filled === 0) {
        return null
    }

    return {filled, required, complete: required > 0 && filled >= required}
}

const CARD_ACTION = {
    notifyDrivers: DRIVER_SEND_STATUS.notifyDrivers,
    assignDrivers: 'Assign drivers',
    assignEquipment: 'Assign equipment',
    assignCrew: 'Assign crew',
    confirmOut: 'Confirm equipment out',
} as const

function cardActionCta(
    lane: BoardLane,
    operational: OperationalState,
    drivers: ResourceProgress | null,
    equipment: ResourceProgress | null,
    unnotifiedDriverCount: number,
    notConfirmedCount: number,
): {line: string; tone: StatusTone} {
    if (lane === 'needs_action') {
        const driversIncomplete = drivers != null && !drivers.complete
        const equipmentIncomplete = equipment != null && !equipment.complete

        if (driversIncomplete && equipmentIncomplete) {
            return {line: CARD_ACTION.assignCrew, tone: 'critical'}
        }
        if (driversIncomplete) {
            return {line: CARD_ACTION.assignDrivers, tone: 'critical'}
        }
        if (equipmentIncomplete) {
            return {line: CARD_ACTION.assignEquipment, tone: 'critical'}
        }

        if (unnotifiedDriverCount > 0 || operational.nextAction === 'notification') {
            return {line: CARD_ACTION.notifyDrivers, tone: 'critical'}
        }
        if (notConfirmedCount > 0) {
            return {line: CARD_ACTION.confirmOut, tone: 'critical'}
        }

        return {line: CARD_ACTION.assignCrew, tone: 'critical'}
    }

    if (lane === 'notify_drivers') {
        return {line: operational.ownershipLabel, tone: 'attention'}
    }

    return {line: 'Dispatched', tone: 'calm'}
}

function locationLine(job: TimelineJobRow): string | undefined {
    const {projectName, siteName} = job.boardScenario ?? {}
    const parts = [projectName, siteName].filter(Boolean)
    return parts.length > 0 ? parts.join(' · ') : undefined
}

function crewLine(
    display: CommitmentJobDisplay,
    drivers: ResourceProgress | null,
    equipment: ResourceProgress | null,
): string | undefined {
    if (drivers != null || equipment != null) {
        return undefined
    }

    if (display.requirementCategorySummaryLine) {
        return display.requirementCategorySummaryLine
    }
    if (display.assignedNames.length > 0) {
        return display.assignedNames.join(', ')
    }
    return undefined
}

export function buildJobCardSummary(
    entry: TerminalRowEntry,
    horizon: JobCardHorizonContext,
): JobCardSummary {
    const {job, display, operational, lane, viewDayOffset, isContinuationDay} = entry
    const drivers = progressForKind(job, 'worker')
    const equipment = progressForKind(job, 'rental_asset')
    const notConfirmedCount = job.unconfirmedRentalCount ?? 0
    const status = cardActionCta(
        lane,
        operational,
        drivers,
        equipment,
        display.unnotifiedDriverCount,
        notConfirmedCount,
    )
    const schedule = terminalScheduleLabelFromContext(
        job,
        display,
        {
            visibleDays: horizon.visibleDays,
            viewRange: horizon.viewRange,
            nowHour: horizon.nowHour,
        },
        {
            viewDayOffset,
            isContinuationDay,
        },
    )

    return {
        title: display.title,
        jobCode: display.jobCode,
        customer: display.customer,
        internalContactName: job.internalContactName ?? null,
        scheduleAnchor: schedule.anchor,
        scheduleStatus: schedule.status,
        scheduleDuration: schedule.duration,
        scheduleIsTime: schedule.secondaryKind === 'time',
        spansMultipleDays: display.spansMultipleDays,
        lane,
        locationLine: locationLine(job),
        crewLine: crewLine(display, drivers, equipment),
        drivers,
        equipment,
        assignedNames: display.assignedNames,
        outstanding: display.missingLabels,
        unnotifiedDriverCount: display.unnotifiedDriverCount,
        statusLine: status.line,
        statusTone: status.tone,
        ...(job.partialPickup ? {partialPickupLine: partialPickupText(job.partialPickup)} : {}),
        ...(job.outOfStateLabel ? {outOfStateLabel: job.outOfStateLabel} : {}),
        ...(job.noYard ? {noYard: true} : {}),
        ...(notConfirmedCount > 0 ? {notConfirmedCount} : {}),
    }
}
